import { apiClient } from './index';

export interface Phase {
  id: string;
  project_id: string;
  tenant_id: string;
  name: string;
  position: number;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface ScheduledTask {
  task_id: string;
  start: string;   // YYYY-MM-DD
  due: string;     // YYYY-MM-DD
  slack_days: number;
  is_critical: boolean;
}

export interface MilestoneProjection {
  milestone_id: string;
  name: string;
  target?: string | null;
  projected?: string | null;
  float_days?: number | null;
  health: 'on_track' | 'late' | 'done' | 'no_target';
}

export interface PhaseRollup {
  phase_id: string;
  start?: string | null;
  end?: string | null;
  task_count: number;
  done_count: number;
}

export interface Schedule {
  tasks: ScheduledTask[];
  end_date: string;
  critical_path: string[];
  milestones?: MilestoneProjection[];
  phases?: PhaseRollup[];
}

export async function getPhases(projectId: string): Promise<Phase[]> {
  const { data } = await apiClient.get<{ data: Phase[] }>(`/projects/${projectId}/phases`);
  return data.data ?? [];
}

export async function createPhase(projectId: string, input: { name: string; color?: string }): Promise<Phase> {
  const { data } = await apiClient.post<Phase>(`/projects/${projectId}/phases`, input);
  return data;
}

export async function updatePhase(phaseId: string, input: { name?: string; color?: string }): Promise<Phase> {
  const { data } = await apiClient.patch<Phase>(`/phases/${phaseId}`, input);
  return data;
}

export async function reorderPhases(projectId: string, phaseIds: string[]): Promise<void> {
  await apiClient.post(`/projects/${projectId}/phases/reorder`, { phase_ids: phaseIds });
}

export async function deletePhase(phaseId: string): Promise<void> {
  await apiClient.delete(`/phases/${phaseId}`);
}

export async function schedulePlan(projectId: string, apply: boolean): Promise<Schedule> {
  const { data } = await apiClient.post<Schedule>(`/projects/${projectId}/plan/schedule`, { apply });
  return data;
}

export async function generatePlan(projectId: string, prompt: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/ai/generate-plan`, { prompt, create: true });
}

export interface Baseline {
  id: string;
  label: string;
  created_at?: string;
}

export interface TaskVariance {
  task_id: string;
  title: string;
  baseline_start?: string | null;
  baseline_due?: string | null;
  scheduled_start?: string | null;
  scheduled_due?: string | null;
  actual_start?: string | null;
  actual_finish?: string | null;
  slip_days?: number | null;
}

export interface MilestoneVariance {
  milestone_id: string;
  name: string;
  baseline_target?: string | null;
  current_due?: string | null;
  slip_days?: number | null;
}

export interface Variance {
  tasks: TaskVariance[];
  milestones: MilestoneVariance[];
  baseline?: Baseline;
}

export async function listBaselines(projectId: string): Promise<Baseline[]> {
  const { data } = await apiClient.get<{ data: Baseline[] }>(`/projects/${projectId}/plan/baselines`);
  return data.data ?? [];
}

export async function createBaseline(projectId: string, label?: string): Promise<Baseline> {
  const { data } = await apiClient.post<Baseline>(`/projects/${projectId}/plan/baselines`, { label: label ?? '' });
  return data;
}

export async function deleteBaseline(projectId: string, baselineId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/plan/baselines/${baselineId}`);
}

export async function getVariance(projectId: string, baselineId: string): Promise<Variance> {
  const { data } = await apiClient.get<Variance>(`/projects/${projectId}/plan/variance`, { params: { baseline_id: baselineId } });
  return data;
}

export async function addMilestoneDependency(projectId: string, milestoneId: string, taskId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/milestones/${milestoneId}/dependencies`, { task_id: taskId });
}

export async function removeMilestoneDependency(projectId: string, milestoneId: string, taskId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}/milestones/${milestoneId}/dependencies/${taskId}`);
}
